<template>
  <div class="sales-by-variant">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-chart-scatter-plot"></i> Sales by Variant Analysis
            </h2>
            <p class="text-white opacity-75">Comprehensive variant performance analysis and insights</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Advanced Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
                />
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Variants">
                <el-select
                  v-model="filters.variant_ids"
                  multiple
                  filterable
                  placeholder="All Variants"
                  @change="loadVariantData"
                >
                  <el-option
                    v-for="variant in variants"
                    :key="variant.id"
                    :label="variant.name"
                    :value="variant.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items">
                <el-select
                  v-model="filters.item_ids"
                  multiple
                  filterable
                  placeholder="All Items"
                  @change="loadVariantData"
                >
                  <el-option
                    v-for="item in items"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employees">
                <el-select
                  v-model="filters.user_id"
                  multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadVariantData"
                >
                  <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Customers">
                <el-select
                  v-model="filters.customer_id"
                  multiple
                  filterable
                  placeholder="All Customers"
                  @change="loadVariantData"
                >
                  <el-option
                    v-for="customer in customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Methods">
                <el-select
                  v-model="filters.payment_method_id"
                  multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadVariantData"
                >
                  <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Analysis Type">
                <el-select
                  v-model="filters.analysis_type"
                  placeholder="Analysis Type"
                  @change="loadVariantData"
                >
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Quantity" value="quantity"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select
                  v-model="filters.sort_by"
                  placeholder="Sort by"
                  @change="loadVariantData"
                >
                  <el-option label="Revenue (High to Low)" value="total_revenue_desc"></el-option>
                  <el-option label="Revenue (Low to High)" value="total_revenue_asc"></el-option>
                  <el-option label="Quantity (High to Low)" value="total_quantity_desc"></el-option>
                  <el-option label="Quantity (Low to High)" value="total_quantity_asc"></el-option>
                  <el-option label="Orders (High to Low)" value="total_orders_desc"></el-option>
                  <el-option label="Orders (Low to High)" value="total_orders_asc"></el-option>
                  <el-option label="Variant Name" value="variant_name"></el-option>
                  <el-option label="Item Name" value="item_name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadVariantData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- Summary Statistics -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison">
                  <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs previous period</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading variant analysis...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Variant Performance Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Variant Performance</h4>
                <el-select v-model="chartView" size="mini" @change="updateChartData">
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Quantity" value="quantity"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="variantData.length > 0"
                  :chart-data="variantChartData"
                  :chart-options="variantChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-bar" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Variant Distribution Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Variant Distribution</h4>
                <el-switch v-model="showPercentage" active-text="%" inactive-text="Value"/>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="variantData.length > 0"
                  :chart-data="distributionChartData"
                  :chart-options="distributionChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-pie" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Trends and Comparison -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Variant Trends -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Variant Trends</h4>
                <el-select v-model="trendPeriod" size="mini" @change="loadTrendsData">
                  <el-option label="Daily" value="daily"></el-option>
                  <el-option label="Weekly" value="weekly"></el-option>
                  <el-option label="Monthly" value="monthly"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No trends data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Top Variants -->
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-trophy"></i> Top Performing Variants</h4>
              </div>
              <div class="top-variants-list">
                <div
                  v-for="(variant, index) in topVariants"
                  :key="variant.variant_id"
                  class="variant-item"
                >
                  <div class="variant-rank">{{ index + 1 }}</div>
                  <div class="variant-info">
                    <div class="variant-name">{{ variant.variant_name }}</div>
                    <div class="variant-stats">
                      <span class="revenue">{{ formatCurrency(variant.total_revenue) }}</span>
                      <span class="quantity">{{ variant.total_quantity }} units</span>
                    </div>
                    <el-progress
                      :percentage="(variant.total_revenue / Math.max(...topVariants.map(v => v.total_revenue))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="variant-trend">
                    <span :class="['growth', variant.growth_rate > 0 ? 'positive' : 'negative']">
                      <i :class="variant.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(variant.growth_rate) }}%
                    </span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Variant Data Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Variant Analysis</h4>
            <div class="header-actions">
              <el-tag type="info">{{ pagination.total }} variants found</el-tag>
              <el-button-group>
                <el-button size="mini" @click="toggleViewMode">
                  <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                  {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="viewMode === 'table'">
            <el-table
              :data="variantData"
              style="width: 100%"
              size="small"
              @sort-change="handleSortChange"
              @selection-change="handleSelectionChange"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="variant_name" label="Variant Name" width="200" />
              <el-table-column prop="item_name" label="Item Name" width="200" />
              <el-table-column prop="item_code" label="Item Code" width="120" />
              <el-table-column label="Revenue" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.total_revenue) }}
                </template>
              </el-table-column>
              <el-table-column label="Quantity" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_quantity.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Orders" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_orders.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Avg Price" width="120">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.average_price) }}
                </template>
              </el-table-column>
              <el-table-column label="Revenue Share" width="120">
                <template slot-scope="scope">
                  {{ scope.row.revenue_share.toFixed(1) }}%
                </template>
              </el-table-column>
              <el-table-column label="Growth" width="100">
                <template slot-scope="scope">
                  <span :class="['growth', scope.row.growth_rate > 0 ? 'positive' : 'negative']">
                    <i :class="scope.row.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(scope.row.growth_rate) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-view"
                      @click="viewVariantDetails(scope.row)"
                    />
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-chart"
                      @click="viewVariantTrends(scope.row)"
                    />
                  </el-button-group>
                </template>
              </el-table-column>
            </el-table>

            <!-- Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.current_page"
                :page-sizes="[10, 25, 50, 100]"
                :page-size="+pagination.per_page"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              />
            </div>
          </div>

          <!-- Grid View -->
          <div v-else class="grid-view">
            <el-row :gutter="20">
              <el-col
                v-for="variant in variantData"
                :key="variant.variant_id"
                :xs="24"
                :sm="12"
                :md="8"
                :lg="6"
                class="m-b-20"
              >
                <el-card :body-style="{ padding: '15px' }" class="variant-card">
                  <div class="variant-header">
                    <h4 class="variant-name">{{ variant.variant_name }}</h4>
                    <el-tag :type="getVariantStatusType(variant)" size="mini">
                      {{ getVariantStatusText(variant) }}
                    </el-tag>
                  </div>

                  <div class="variant-info">
                    <div class="info-row">
                      <span class="label">Item:</span>
                      <span class="value">{{ variant.item_name }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Code:</span>
                      <span class="value">{{ variant.item_code }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Revenue:</span>
                      <span class="value amount">{{ formatCurrency(variant.total_revenue) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Quantity:</span>
                      <span class="value">{{ variant.total_quantity.toLocaleString() }} units</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Orders:</span>
                      <span class="value">{{ variant.total_orders.toLocaleString() }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Avg Price:</span>
                      <span class="value">{{ formatCurrency(variant.average_price) }}</span>
                    </div>
                  </div>

                  <div class="variant-footer">
                    <span class="growth" :class="variant.growth_rate > 0 ? 'positive' : 'negative'">
                      <i :class="variant.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(variant.growth_rate) }}%
                    </span>
                    <div class="actions">
                      <el-button size="mini" type="primary" icon="el-icon-view" @click="viewVariantDetails(variant)" />
                      <el-button size="mini" type="success" icon="el-icon-chart" @click="viewVariantTrends(variant)" />
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Variant Details Dialog -->
    <el-dialog
      title="Variant Details"
      :visible.sync="variantDetailsVisible"
      width="70%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedVariantDetails" class="variant-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Variant Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Variant Name">{{ selectedVariantDetails.variant.name }}</el-descriptions-item>
              <el-descriptions-item label="Item Name">{{ selectedVariantDetails.variant.item_name || 'N/A' }}</el-descriptions-item>
              <el-descriptions-item label="Created">{{ formatDate(selectedVariantDetails.variant.created_at) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>

          <el-col :span="12">
            <h4>Performance Metrics</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Total Revenue">{{ formatCurrency(selectedVariantDetails.sales_stats.total_revenue) }}</el-descriptions-item>
              <el-descriptions-item label="Total Quantity">{{ selectedVariantDetails.sales_stats.total_quantity.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Total Orders">{{ selectedVariantDetails.sales_stats.total_orders.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Average Price">{{ formatCurrency(selectedVariantDetails.sales_stats.average_price) }}</el-descriptions-item>
              <el-descriptions-item label="Last Sale">{{ formatDate(selectedVariantDetails.sales_stats.last_sale_date) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
        </el-row>

        <el-divider content-position="left">Top Items</el-divider>
        <el-table :data="selectedVariantDetails.top_items || []" size="small">
          <el-table-column prop="item_name" label="Item" />
          <el-table-column prop="total_quantity" label="Qty" width="80" />
          <el-table-column prop="total_revenue" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_revenue) }}
            </template>
          </el-table-column>
          <el-table-column prop="total_orders" label="Orders" width="100" />
        </el-table>

        <el-divider content-position="left">Top Employees</el-divider>
        <el-table :data="selectedVariantDetails.top_employees || []" size="small">
          <el-table-column prop="user_name" label="Employee" />
          <el-table-column prop="total_orders" label="Orders" width="80" />
          <el-table-column prop="total_revenue" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_revenue) }}
            </template>
          </el-table-column>
          <el-table-column prop="total_quantity" label="Quantity" width="100" />
        </el-table>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="variantDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="viewVariantTrends(selectedVariant)">View Trends</el-button>
        <el-button type="success" @click="exportVariantReport(selectedVariant)">Export Report</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'SalesByVariant',
  components: {
    BarChart,
    DoughnutChart,
    LineChart
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      variantDetailsVisible: false,
      selectedVariant: null,
      selectedVariantDetails: null,
      selectedVariants: [],
      chartView: 'revenue',
      showPercentage: false,
      trendPeriod: 'monthly',
      variantData: [],
      trendsData: [],
      variants: [],
      items: [],
      users: [],
      customers: [],
      paymentMethods: [],
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        variant_ids: [],
        item_ids: [],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        analysis_type: 'revenue',
        sort_by: 'total_revenue_desc'
      },
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      },
      dateShortcuts: [{
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'Last 3 months',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 3)
          return [start, end]
        }
      }, {
        text: 'This year',
        value: () => {
          const end = new Date()
          const start = new Date(new Date().getFullYear(), 0, 1)
          return [start, end]
        }
      }]
    }
  },
  computed: {
    summaryStats () {
      const totalRevenue = this.variantData.reduce((sum, variant) => sum + variant.total_revenue, 0)
      const totalQuantity = this.variantData.reduce((sum, variant) => sum + variant.total_quantity, 0)
      const totalOrders = this.variantData.reduce((sum, variant) => sum + variant.total_orders, 0)
      const avgGrowth = this.variantData.length > 0
        ? this.variantData.reduce((sum, variant) => sum + variant.growth_rate, 0) / this.variantData.length
        : 0

      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Quantity',
          value: totalQuantity.toLocaleString(),
          trend: 8.3,
          icon: 'mdi mdi-package-variant'
        },
        {
          title: 'Total Orders',
          value: totalOrders.toLocaleString(),
          trend: 5.2,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Growth',
          value: avgGrowth.toFixed(1) + '%',
          trend: avgGrowth,
          icon: 'mdi mdi-trending-up'
        }
      ]
    },
    topVariants () {
      return this.variantData.slice(0, 5)
    },
    variantChartData () {
      const labels = this.variantData.slice(0, 10).map(variant => variant.variant_name)
      const data = this.variantData.slice(0, 10).map(variant => {
        switch (this.chartView) {
          case 'revenue':
            return variant.total_revenue
          case 'quantity':
            return variant.total_quantity
          case 'orders':
            return variant.total_orders
          default:
            return variant.total_revenue
        }
      })

      return {
        labels: labels,
        datasets: [{
          label: this.chartView.charAt(0).toUpperCase() + this.chartView.slice(1),
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: this.getChartColors(data.length),
          borderWidth: 1
        }]
      }
    },
    variantChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (this.chartView === 'revenue') {
                  return this.formatCurrency(value)
                }
                return value.toLocaleString()
              }.bind(this)
            }
          }
        }
      }
    },
    distributionChartData () {
      const labels = this.variantData.slice(0, 8).map(variant => variant.variant_name)
      const data = this.variantData.slice(0, 8).map(variant => {
        if (this.showPercentage) {
          const total = this.variantData.reduce((sum, v) => sum + v.total_revenue, 0)
          return ((variant.total_revenue / total) * 100).toFixed(1)
        }
        return variant.total_revenue
      })

      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },
    distributionChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => this.formatDate(item.date)),
        datasets: this.variantData.slice(0, 5).map((variant, index) => ({
          label: variant.variant_name,
          data: this.trendsData.map(period =>
            period.variants?.find(v => v.variant_id === variant.variant_id)?.revenue || 0
          ),
          borderColor: this.getChartColors(5)[index],
          backgroundColor: this.getChartColors(5)[index] + '20',
          tension: 0.4
        }))
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top'
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        variant_ids: [],
        item_ids: [],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        analysis_type: 'revenue',
        sort_by: 'total_revenue_desc'
      }
      this.pagination.current_page = 1
      this.loadVariantData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadVariants(),
          this.loadItems(),
          this.loadUsers(),
          this.loadCustomers(),
          this.loadPaymentMethods()
        ])
        await this.loadVariantData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadVariantData () {
      this.loading = true
      try {
        const params = {
          ...this.filters,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }

        const response = await this.$reportService.getSalesByVariant(params)
        if (response.status === 'success') {
          this.variantData = response.data
          this.pagination = {
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load variant data')
      } finally {
        this.loading = false
      }
    },
    async loadTrendsData () {
      try {
        const params = {
          ...this.filters,
          period: this.trendPeriod,
          per_page: 1000,
          page: 1
        }

        const response = await this.$reportService.getVariantTrends(params)
        if (response.status === 'success') {
          this.trendsData = response.data
        }
      } catch (error) {
        console.error('Error loading trends data:', error)
      }
    },
    async loadVariants () {
      try {
        this.variants = await this.$variantService.getVariants()
      } catch (error) {
        console.error('Error loading variants:', error)
      }
    },
    async loadItems () {
      try {
        this.items = await this.$itemService.getItems()
      } catch (error) {
        console.error('Error loading items:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const params = {
          ...this.filters,
          dl: 'pdf'
        }

        const response = await this.$reportService.getSalesByVariant(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    updateChartData () {
      // Chart data will be updated automatically through computed properties
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadVariantData()
    },
    handleSelectionChange (selection) {
      this.selectedVariants = selection
    },
    handleSizeChange (size) {
      this.pagination.per_page = size
      this.loadVariantData()
    },
    handleCurrentChange (page) {
      this.pagination.current_page = page
      this.loadVariantData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewVariantDetails (variant) {
      this.selectedVariant = variant
      this.variantDetailsVisible = true
      this.loadVariantDetails(variant)
    },
    async loadVariantDetails (variant) {
      try {
        const response = await this.$reportService.getVariantDetails({ variant_id: variant.variant_id, ...this.filters })
        if (response.status === 'success') {
          this.selectedVariantDetails = response.data
        }
      } catch (error) {
        this.handleError(error, 'Failed to load variant details')
      }
    },
    viewVariantTrends (variant) {
      // Navigate to variant trends or open in modal
      console.log('Viewing trends for variant:', variant.variant_name)
    },
    exportVariantReport (variant) {
      // Implement variant-specific report export
      console.log('Exporting report for variant:', variant.variant_name)
    },
    handleCloseDialog () {
      this.variantDetailsVisible = false
      this.selectedVariant = null
      this.selectedVariantDetails = null
    },
    getVariantStatusType (variant) {
      if (variant.growth_rate > 10) return 'success'
      if (variant.growth_rate > 0) return 'warning'
      return 'danger'
    },
    getVariantStatusText (variant) {
      if (variant.growth_rate > 10) return 'High Growth'
      if (variant.growth_rate > 0) return 'Growing'
      return 'Declining'
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },
    getChartColors (count) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#9C27B0', '#FF9800', '#795548', '#607D8B', '#FF5722'
      ]
      return colors.slice(0, count)
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.sales-by-variant {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info {
  flex: 1;
}

.stat-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-container {
  position: relative;
  height: 300px;
}

.top-variants-list {
  max-height: 300px;
  overflow-y: auto;
}

.variant-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.variant-item:last-child {
  border-bottom: none;
}

.variant-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.variant-info {
  flex: 1;
  margin-right: 12px;
}

.variant-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.variant-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.variant-trend {
  text-align: right;
  font-size: 12px;
}

.growth {
  font-weight: bold;
}

.growth.positive {
  color: #67C23A;
}

.growth.negative {
  color: #F56C6C;
}

.grid-view {
  margin-top: 20px;
}

.variant-card {
  transition: all 0.3s ease;
}

.variant-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.variant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.variant-name {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.variant-info {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.label {
  font-size: 12px;
  color: #909399;
}

.value {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
}

.value.amount {
  color: #409EFF;
  font-weight: bold;
}

.variant-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
}

.actions {
  display: flex;
  gap: 5px;
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
}

.text-muted {
  color: #909399;
}

.p-40 {
  padding: 40px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.m-b-30 {
  margin-bottom: 30px;
}

.p-b-30 {
  padding-bottom: 30px;
}

.p-t-30 {
  padding-top: 30px;
}
</style>
